<template>
  <tab-layout :full-width="true">
    <div slot="tabContent" class="bg-white rounded mb-4 p-3">
      <page-heading>Draws Dashboard</page-heading>

      <div class="alert alert-info">
        Draw Generation Checklist:
        <ol class="mb-0 mt-1">
          <li>Have you moved your draft to closed status? Draws will not generate unless the draft is in closed status.</li>
          <li>If you have split an event into A/B drafts ensure you have moved competitors between these two events where necessary before draw generation.</li>
          <li>Have you scratched all unpaid runs you do not wish to appear in the draw? If a competitor has not paid and you do not wish them to be in the draw please scratch them.</li>
        </ol>
      </div>

      <div class="alert alert-info">
        For assistance during regular business hours (Monday-Friday 8:30am-4pm AEST) contact the ACA Office at 07 4622 3110.
        For assistance after-hours for the duration of you draft only call the Campdraft One After-Hours line at 07 4622 7110.
        This line is only attended for the duration of your draft and will not work before or after your draft has commenced.
      </div>

      <div class="alert alert-warning text-center">
        <span class="draws-dashboard__warn-icon" />
        IF YOU DO NOT MARK YOUR EVENT MEMBERS AS PAID IN THE SEPARATE EVENT MEMBER AREA THEY WILL NOT SHOW IN THE DRAW.
      </div>

      <div class="mb-3">
        <label class="font-weight-bold">Filter by year</label>
        <div style="max-width: 120px">
          <f-search-select
            id="draws_dashboard_year"
            :value="year"
            :options="years.map(y => ({ id: y, name: String(y) }))"
            :searchable="false"
            @input="setYear(Number($event))"
          />
        </div>
      </div>

      <div v-for="draft in drafts" :key="draft.id" class="mb-5">
        <form-alert
          :message="draftAlert.draftId === draft.id ? draftAlert.message : null"
          :type="draftAlert.type"
        />
        <div v-if="draft.event_members_allowed" class="alert alert-warning mb-0 rounded-0">
            Click <router-link :to="`/draft-members/${draft.id}`" class="text-primary">here</router-link> to mark event members as paid.
        </div>
        <div class="draws-dashboard__draft">
          
          <div class="draws-dashboard__header">
            <div class="draws-dashboard__header-left">
              <span class="draws-dashboard__draft-name">{{ draft.name }}</span>
              <div v-if="draft.start_date" class="draws-dashboard__draft-date">{{ formatDateTime(draft.start_date) }}</div>
            </div>
            <div class="draws-dashboard__header-actions">
              <app-button v-if="canPublishAll(draft)" size="sm" :block="false" :disabled="draft.is_generating" @click="onPublishAll(draft.id)">
                Publish all draws
              </app-button>
            </div>
            <div class="draws-dashboard__header-right">
              <app-button v-if="canGenerateAll(draft)" size="sm" :block="false" :disabled="draft.is_generating" @click="onGenerateAll(draft.id)">
                Generate all draws
              </app-button>
              <router-link v-if="draft.has_calc_places" :to="`/drafts/${draft.id}/prize-summary`" class="btn btn-sm btn-primary text-nowrap">
                PRIZE MONEY SUMMARY
              </router-link>
            </div>
          </div>
          <ul v-if="draft.events && draft.events.length" class="draws-dashboard__events">
            <template v-for="{ event, boundaryTop, boundaryBottom } in eventRows(draft)">
              <li
                :key="event.id"
                class="draws-dashboard__event"
                :class="{ 'draws-dashboard__event--boundary': boundaryTop, 'draws-dashboard__event--boundary-end': boundaryBottom }"
              >
                <div class="draws-dashboard__event-info">
                  <template v-if="eventDrawsCount(event) > 0">
                    <router-link :to="eventLink(draft.id, event)">
                      {{ event.name }} ({{ eventDrawsCount(event) }})
                    </router-link>
                  </template>
                  <span v-else>{{ event.name }}</span>
                </div>
                <div class="draws-dashboard__event-actions">
                  <app-button
                    v-if="canGenerateEvent(draft, event)"
                    variant="outline-primary"
                    size="sm"
                    :disabled="draft.is_generating"
                    @click="onGenerateEvent(event.id, draft.id)"
                  >
                    Generate event draws
                  </app-button>
                  <router-link v-if="event.requires_horse && event.calculator_id && event.calculator_initialized" :to="`/prizes/${event.id}`" class="btn btn-sm btn-outline-primary">
                    View results
                  </router-link>
                  <router-link v-if="event.requires_horse && event.calculator_id" :to="`/calculators/${event.id}`" class="btn btn-sm btn-outline-primary">
                    Edit calculations
                  </router-link>
                </div>
              </li>
            </template>
          </ul>
        </div>
      </div>
    </div>

  </tab-layout>
</template>

<script>
import { defineComponent, ref, onMounted } from '@vue/composition-api'
import TabLayout from '@/components/TabLayout'
import PageHeading from '@/components/PageHeading'
import AppButton from '@/components/AppButton'
import FSearchSelect from '@/components/FormFields/FSearchSelect'
import FormAlert from '@/components/FormAlert'
import useDrawsDashboard from '@/composables/draws/useDrawsDashboard'
import getYears from '@/composables/useYears'
import { formatDateTime } from '@/utils/date'
import { DRAFT_STATE_CLOSED } from '@/constants/draftStates'
import { EVENT_SPLIT_KIND_COMBINED_CONJUNCTION_FINAL } from '@/constants/eventSplitKind'
import { buildEventGroups } from '@/utils/eventGroups'

export default defineComponent({
  name: 'DrawsDashboard',
  components: { TabLayout, PageHeading, AppButton, FSearchSelect, FormAlert },
  setup() {
    const draftAlert = ref({ draftId: null, message: null, type: 'success' })
    let alertTimer = null
    const setDraftAlert = (draftId, msg, type) => {
      clearTimeout(alertTimer)
      draftAlert.value = { draftId, message: msg, type }
      alertTimer = setTimeout(() => { draftAlert.value = { draftId: null, message: null, type: 'success' } }, 5000)
    }

    const { drafts, loading, fetchDashboard, publishAll, queueGenerateAll, queueGenerate } = useDrawsDashboard()
    const years = getYears()
    const year = ref(new Date().getFullYear())

    function setYear(val) {
      year.value = val
      fetchDashboard(val)
    }

    async function onPublishAll(draftId) {
      draftAlert.value = { draftId: null, message: null, type: 'success' }
      const result = await publishAll(draftId)
      if (result) setDraftAlert(draftId, result.message, result.type)
      await fetchDashboard(year.value)
    }

    async function onGenerateAll(draftId) {
      draftAlert.value = { draftId: null, message: null, type: 'success' }
      const result = await queueGenerateAll(draftId)
      if (result) setDraftAlert(draftId, result.message, result.type)
      await fetchDashboard(year.value)
    }

    async function onGenerateEvent(eventId, draftId) {
      draftAlert.value = { draftId: null, message: null, type: 'success' }
      const result = await queueGenerate(eventId, draftId)
      if (result) setDraftAlert(draftId, result.message, result.type)
      await fetchDashboard(year.value)
    }

    function isSplitFinal(event) {
      return event.split_kind === EVENT_SPLIT_KIND_COMBINED_CONJUNCTION_FINAL
    }

    function isEventVisible(event) {
      if (!event.requires_horse) return false
      if (event.has_nominations) return true
      if (isSplitFinal(event)) return true
      return event.parent_draws_count > 0
    }

    // Visible events laid out group by group (a split pair, a conjunction pair with its final,
    // combined A/B halves stay together). A heavier divider marks the start of every multi-event
    // group and the first row after one; single events get no extra divider.
    function eventRows(draft) {
      const rows = []
      let previousWasMulti = false

      buildEventGroups(draft.events || []).forEach((group) => {
        const visible = group.units.flatMap(unit => [unit.row, ...unit.children]).filter(isEventVisible)
        const isMulti = visible.length > 1

        visible.forEach((event, index) => {
          rows.push({ event, boundaryTop: index === 0 && (isMulti || previousWasMulti), boundaryBottom: false })
        })

        if (visible.length) previousWasMulti = isMulti
      })

      if (rows.length && previousWasMulti) rows[rows.length - 1].boundaryBottom = true

      return rows
    }

    function eventDrawsCount(event) {
      if (event.draws_count > 0) return event.draws_count
      if (event.parent_draws_count > 0) return event.parent_draws_count
      return 0
    }

    function eventLink(draftId, event) {
      if (!event.requires_horse) return null
      return `/draws/${draftId}/${event.id}/1`
    }

    function canGenerateAll(draft) {
      if (draft.draft_state_id !== DRAFT_STATE_CLOSED) return false
      if (draft.is_generating) return false
      const events = draft.events || []
      return events.length > 0 && events.every(e => e.draws_count === 0)
    }

    function canPublishAll(draft) {
      if (draft.auto_send_texts) return false
      if (draft.events_not_published_count < 1) return false
      if (draft.is_generating) return false
      const events = draft.events || []
      return events.some(e => e.draws_count > 0)
    }

    function canGenerateEvent(draft, event) {
      if (!event.requires_horse) return false
      if (draft.draft_state_id !== DRAFT_STATE_CLOSED) return false
      if (event.draws_count > 0) return false
      if (event.parent_draws_count > 0) return false
      if (isSplitFinal(event)) return false
      return true
    }

    onMounted(() => {
      fetchDashboard(year.value)
    })

    return {
      draftAlert,
      drafts,
      loading,
      years,
      year,
      setYear,
      onPublishAll,
      onGenerateAll,
      onGenerateEvent,
      isEventVisible,
      eventRows,
      eventDrawsCount,
      eventLink,
      canGenerateAll,
      canPublishAll,
      canGenerateEvent,
      formatDateTime
    }
  }
})
</script>

<style scoped>
.draws-dashboard__draft {
  border: 1px solid #dee2e6;
}

.draws-dashboard__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  background-color: #e9ecef;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #dee2e6;
}

.draws-dashboard__header-left {
  flex: 1;
}

.draws-dashboard__header-actions {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  flex: 1;
}

.draws-dashboard__header-right {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.draws-dashboard__draft-name {
  font-size: 1.25rem;
  font-weight: 700;
}

.draws-dashboard__draft-date {
  font-size: 0.95rem;
  color: #6c757d;
}

.draws-dashboard__events {
  list-style: none;
  margin: 0;
  padding: 0;
}

.draws-dashboard__event {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #dee2e6;
}

.draws-dashboard__event:last-child {
  border-bottom: none;
}

.draws-dashboard__event.draws-dashboard__event--boundary {
  border-top: 2px solid var(--gray-400);
  margin-top: -1px;
}

.draws-dashboard__event.draws-dashboard__event--boundary-end {
  border-bottom: 2px solid var(--gray-400);
}

.draws-dashboard__event-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.draws-dashboard__warn-icon {
  display: inline-block;
  background-image: url('~@/assets/img/sprite.png');
  background-position: -34px 0px;
  width: 32px;
  height: 28px;
  vertical-align: middle;
  margin-right: 0.4rem;
}

@media (max-width: 600px) {
  .draws-dashboard__header {
    flex-direction: column;
    align-items: stretch;
  }

  .draws-dashboard__header-actions,
  .draws-dashboard__header-right {
    flex-direction: column;
    justify-content: flex-start;
  }

  .draws-dashboard__header-actions .btn,
  .draws-dashboard__header-right .btn {
    width: 100%;
  }

  .draws-dashboard__event {
    flex-direction: column;
    align-items: stretch;
  }

  .draws-dashboard__event-info {
    text-align: left;
  }

  .draws-dashboard__event-actions {
    flex-direction: column;
    align-items: flex-start;
  }

  .draws-dashboard__event-actions .btn {
    width: 100%;
    text-align: left;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Draws/Dashboard/DrawsDashboard.vue